import {
	Container,
	styled,
	Text,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import {
	Apellido,
	ConsentQuestion,
	Email,
	Error,
	Loading,
	Nombre,
	Telefono,
} from 'src/components';
import {
	CustomFormCss,
	CustomFormStyledWrapper,
} from 'src/components/CustomFormStyles';
import {
	formatPrefix,
	FormController,
	removeWhiteSpace,
	useFaContent,
	useGetInstalaciones,
	useOneFormContext,
} from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { LayoutWrapper } from 'src/feature-app/Screen';
import {
	formatFlags,
	formatHeaders,
	formDataBuilder,
} from 'src/forms/format-fields';
import { MapaLeadsInPage } from 'src/forms/Leads/InPage';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { GTI50LeadConfirmacion, GTI50LeadThankYou } from '.';
import iconRed from './img/icon-red.svg';
import iconWhite from './img/icon-white.svg';

const InputsWrapper = styled.div`
	padding-top: 44px;
	gap: 24px;
	display: flex;
	flex-direction: column;
	width: 100%;
	@media screen and (min-width: 960px) {
		display: flex;
		justify-content: flex-start;
		flex-direction: row;
		flex-wrap: wrap;
		margin-top: 0;
	}

	& > div {
		padding-top: 0 !important;
	}
`;

export function GTI50LeadForm() {
	const { initializeForm } = useOneFormContext();
	const store = useStore();
	const dispatch = useDispatch();
	const faContent = useFaContent();
	const [loading, setLoading] = useState(true);
	const { loading: loadingInstallations, error: errorInstallations } =
		useGetInstalaciones(
			'VW_DDB_FRM_LEM_REC_LEM',
			'VOLKSWAGEN_SF_LEAD_INSTALLATION_VN_VO_SERVICE',
			'vn',
		);

	const faContentDpsFields = (node?: string) => {
		if (node)
			return faContent?.dpsFields?.find(
				(field: any) => field.field === node,
			)?.value;
		return (
			faContent?.dpsFields?.reduce((acc: any, field: any) => {
				acc[field.field] = field.value;
				return acc;
			}, {}) || {}
		);
	};

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<Container>
					<Text bold appearance={TokenTextAppearance.headline300}>
						Como el GTI solo hay uno y tú puedes tener el tuyo
					</Text>

					<Text appearance={TokenTextAppearance.headline200}>
						Déjanos tus datos y da el primer paso para hacerte con
						uno.
					</Text>
				</Container>
			),
			fields: (
				<LayoutWrapper>
					<InputsWrapper className="gti-inputs-wrapper">
						<Nombre />
						<Apellido
							name="surname"
							required
							label="Primer apellido"
						/>
						<Apellido
							name="secondSurname"
							label="Segundo apellido"
							required={false}
						/>
					</InputsWrapper>
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'nombre',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					<Text bold>¿En qué teléfono e email </Text>podemos
					localizarte?
				</>
			),
			fields: (
				<LayoutWrapper>
					<InputsWrapper className="gti-inputs-wrapper">
						<Telefono required paddingTop="0px" />
						<Email required paddingTop="0px" />
					</InputsWrapper>
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'contacto',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', 'email'],
		},
		{
			screenIndex: index++,
			name: 'mapa',
			multiStep: true,
			component: (
				<MapaLeadsInPage
					title={
						<>
							<Container>
								<Text
									bold
									appearance={TokenTextAppearance.headline300}
								>
									Prepárate para lo que viene
								</Text>
							</Container>
							<Container>
								<Text
									appearance={TokenTextAppearance.headline200}
								>
									Elige un concesionario para poder ponernos
									en contacto contigo.
								</Text>
							</Container>
						</>
					}
				/>
			),
		},
		{
			fields: <ConsentQuestion questionType="lssi" icon={iconRed} />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" icon={iconWhite} />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <GTI50LeadConfirmacion />,
			screenIndex: index++,
			name: 'confirmacion',
			isLastStep: true,
			hidePreviousStep: true,
		},
	];

	const sendForm = (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, device } = getParamsForm();
			const { formData, dealersInfo }: OneFormState = store.getState();
			const { fields, headers, flags } = formData;
			const { selectedInstallation } = dealersInfo;

			const data = {
				...formatHeaders(headers),
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[FORM_URL]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[FORM_NAME]': `vw-oneform-${fields.formName}`,
				'headers[FORM_VERSION]': `vw-oneform-${fields.formName}-${process.env.VERSION}`,

				...formatFlags(flags),

				'fields[DPS_BRAND]': 'Volkswagen',
				'fields[SF_LEAD_RECORDTYPE_NAME]': 'Sales Assistant',
				'fields[SF_LEAD_RECORDTYPE_ID]': '0121i000000D7NyAAK',
				'fields[SF_LEAD_SALES_INITIAL_ACTION]':
					'Direct to Sales Assistant',

				'fields[SF_LEAD_INSTALLATION_ADDRESS]':
					selectedInstallation.ShippingStreet,
				'fields[SF_LEAD_INSTALLATION_CODE]':
					selectedInstallation.VGED_DealerCode__c,
				'fields[SF_LEAD_INSTALLATION_ID]': selectedInstallation.Id,
				'fields[SF_LEAD_INSTALLATION_NAME]':
					selectedInstallation.VGED_CommercialName__c,
				'fields[SF_LEAD_INSTALLATION_PROVINCE_CODE]':
					selectedInstallation.ShippingStateCode.toString().padStart(
						2,
						'0',
					),

				'fields[PERSON_NAME]': fields.name,
				'fields[PERSON_SURNAME_1]': fields.surname,
				'fields[PERSON_SURNAME_2]': fields.secondSurname,
				'fields[PERSON_EMAIL]': removeWhiteSpace(fields.email),
				'fields[PERSON_PHONE_MOBILE_FULL]': `${formatPrefix(
					fields.prefix,
				)}${fields.cellphone}`,
				'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
				'fields[PERSON_PHONE_PREFIX]': formatPrefix(fields.prefix),

				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi
					? fields.lssi
					: 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk
					? fields.tmk
					: 'NO INFORMADO',

				//'fields[VEH_INTEREST_ID]': '30602',
				//'fields[VEH_INTEREST_MODEL]': 'Golf',
				//'fields[VEH_INTEREST_FUEL]': 'Combustion',
				//'fields[VEH_INTEREST_NEW_OR_USED]': 'New Car',
				// ...y otros campos que vienen de faContent

				...faContentDpsFields(),
			};

			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formDataInfo = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formDataInfo);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
		};
	};

	const initialFormData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_LEM_REC_LEM',
			FORM_OBJECTIVE: 'Más Información',
			SF_CAMPAIGN_ID: '7011n000000JrSNAA0',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
			FLAG_IS_MYSTERY_LEAD: 'false',
			FLAG_RELACION_MARCA_SIN_IC: 'false',
			FLAG_SF_INTEGRATION: 'true',
			FLAG_ALLOWS_DATA_ENRICHMENT: 'false',
		},
		fields: {
			formularioFlag: 'masInformacion',
			formName: 'lead-mas_informacion',
			formType: 'contact',
			pageCategory: 'Más Información',
		}, // Analítica
	};

	const { firstStep, lastStep, stepsHistory, numberOfScreens } =
		getFormInfo();

	const formInfoConfig: Partial<FormInfo> = {
		formStarted: true,
		sendFormCallback: sendForm,
		fullScreen: false,
		lastStep,
		numberOfScreens,
		notALayer: false,
		formTheme: 'inverted',
		formType: 'inPage',
		screenIndex: steps[0].screenIndex,
		step: firstStep,
		steps,
		stepsHistory,
		thankYouComponent: <GTI50LeadThankYou />,
		hideLegal: true,
	};

	useEffect(() => {
		setLoading(false);
		initializeForm(formInfoConfig, initialFormData);
	}, []);

	const formStyles = {
		primaryColor: '#c90010',
		fontColor: '#ffffff',
		backgroundColor: '#000000',
		backgroundImage:
			'https://assets.volkswagen.com/is/image/volkswagenag/textura-negra2?Zml0PWNyb3AlMkMxJndpZD0xOTIwJmhlaT0xMDgwJmZtdD13ZWJwJnFsdD03OSZiZmM9b2ZmJmJmMTc=',
	};

	useEffect(() => {
		if (document.getElementById('oneform-gti50-style')) return;
		const style = document.createElement('style');
		style.id = 'oneform-gti50-style';
		style.innerHTML = CustomFormCss(formStyles);
		document.head.appendChild(style);
	});

	return errorInstallations ? (
		<CustomFormStyledWrapper
			fontColor={formStyles.fontColor}
			backgroundColor={formStyles.backgroundColor}
			primaryColor={formStyles.primaryColor}
			backgroundImage={formStyles.backgroundImage}
		>
			<Error />
		</CustomFormStyledWrapper>
	) : loading || loadingInstallations ? (
		<Loading />
	) : (
		<CustomFormStyledWrapper
			fontColor="#ffffff"
			backgroundColor={formStyles.backgroundColor}
			primaryColor={formStyles.primaryColor}
			backgroundImage={formStyles.backgroundImage}
		>
			<FormController steps={steps} screenType={'full-screen'} />
		</CustomFormStyledWrapper>
	);
}
